import React from 'react';
import PropTypes from 'prop-types';
import { find, get } from 'lodash';
import {
  QUESTION_SINGLE,
  QUESTION_RATING,
  QUESTION_SLIDER,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERICAL_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_VAN_WESTENDORP,
  QUESTION_MATRIX_MAXDIFF,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
} from '../../constants';
import InputNumeric from './InputNumeric';
import RatingStars from './RatingStars';
import OpenEnded from './OpenEnded';
import PostalSearch from './PostalSearch';
import DrilldownQuestion from './DrilldownQuestion';
import Ranking from './Ranking';
import Matrix from './Matrix';
import Slider from './Slider';
import MatrixMaxDiff from './MatrixMaxDiff';
import MatrixVanWestendorp from './MatrixVanWestendorp';
import MatrixBipolar from './Matrix/MatrixBipolar';
import MatrixOpenEnded from './Matrix/MatrixOpenEnded';

export default function QuestionBody({ question }) {
  const { getText } = Pollfish.ui;
  if (question.f_name === 'dg_year_of_birth') {
    // we want to handle minimum age as a screen out reason
    // USER_UNDERAGE instead of throwing an error but we also don't want
    // to allow for validation errors, so any age that is 5 years old or younger will
    // throw a validation error
    const ageMax = 5;
    const ageMin = get(
      question,
      `answers[${question.answers.length - 1}].body`,
      '< 1935',
    ).split('< ')[1];
    const date = new Date();
    // eslint-disable-next-line no-param-reassign
    question.age = {
      // even though it says < 1935 it wants it to mean <= 1935. :(
      min: parseInt(ageMin, 10) + 1,
      max: date.getFullYear() - ageMax,
    };
    return <InputNumeric min={question.min} max={question.max} />;
  }

  if (question.style === QUESTION_RATING && question.answers.length < 5) {
    // eslint-disable-next-line no-param-reassign
    question.style = QUESTION_SINGLE;
  }
  const isRtlEnabled = $('html').attr('dir') === 'rtl';
  switch (question.style) {
    case QUESTION_RATING:
      return <RatingStars question={question} />;
    case QUESTION_SLIDER:
      return <Slider question={question} rtl={isRtlEnabled} />;
    case QUESTION_OPEN_ENDED:
      if (question.f_name === 'dg_postal_data') {
        return <PostalSearch />;
      }
      return <OpenEnded question={question} />;
    case QUESTION_NUMERICAL_OPEN_ENDED:
      return <InputNumeric min={question.min} max={question.max} />;
    case QUESTION_RANKING:
      return <Ranking question={question} getText={getText} />;
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      return <Matrix question={question} getText={getText} />;
    case QUESTION_MATRIX_VAN_WESTENDORP:
      return <MatrixVanWestendorp question={question} />;
    case QUESTION_MATRIX_MAXDIFF: {
      const group = find(Pollfish.survey.groups, [
        'refId',
        question.groupRefId,
      ]);
      if (!group) {
        throw new Error('Could not find group from refId');
      }
      const { currentChoiceSet } = group;
      if (!currentChoiceSet) {
        throw new Error('Missing a current concept set for group');
      }
      return (
        <MatrixMaxDiff
          question={question}
          currentChoiceSet={currentChoiceSet}
        />
      );
    }
    case QUESTION_MATRIX_BIPOLAR:
      return <MatrixBipolar question={question} rtl={isRtlEnabled} />;
    case QUESTION_DRILLDOWN:
      return <DrilldownQuestion question={question} />;
    case QUESTION_MATRIX_OPEN_ENDED:
    case QUESTION_MATRIX_OPEN_ENDED_NUMERIC:
    case QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM:
      return <MatrixOpenEnded question={question} getText={getText} />;
    default:
      return null;
  }
}

QuestionBody.propTypes = {
  question: PropTypes.object.isRequired,
};
